import React from "react";
import { Autocomplete, TextField } from "@mui/material";

// --- CONSTANTS ---
const PERSPECTIVES = [
  "Strategic Initiatives",
  "Strategic Projects",
  "Finance Viability",
  "Internal Process",
  "Internal StakeHolder",
  "External StakeHolder",
  "Compliance",
  "Audit",
];
const VALID_FORMULAS = ["regular", "reverse"];
const FREQUENCY_OPTIONS = ["Monthly", "Quarterly", "Semi Annual", "Annual"];
const ACCOUNTABILITIES = ["Prime", "Shared", "Contributory", "Remote"];
const COMPETENCY_CRITERIA = ["Core", "Technical", "Leadership"];

// Add this helper for Date Parsing in Scorecards
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split(/[\/-]/);
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return new Date(year, month - 1, day).toISOString();
  }
  return new Date().toISOString(); // Fallback
};

// --- HELPER: Generic Dropdown ---
const RenderDropdown = (params, options, freeSolo = false) => {
  const { id, field, value, api } = params;
  return (
    <Autocomplete
      fullWidth
      freeSolo={freeSolo}
      options={options || []}
      value={value || ""}
      onChange={(_, newValue) => {
        api.setEditCellValue({ id, field, value: newValue }, { target: { value: newValue } });
      }}
      renderInput={(p) => <TextField {...p} size="small" />}
    />
  );
};

export const IMPORT_STRATEGIES = {
  // =========================
  // 1. AREAS
  // =========================
  areas: {
    requiredFields: ["Area Name", "Latitude", "Longitude"],
    initTrackers: () => ({ ids: new Set(), names: new Set() }),

    getColumns: () => [
      { field: "Area ID", headerName: "ID", width: 90, editable: true, type: "number" },
      { field: "Area Name", headerName: "Name", flex: 1, editable: true },
      { field: "Latitude", headerName: "Latitude", width: 120, editable: true },
      { field: "Longitude", headerName: "Longitude", width: 120, editable: true },
    ],

    validateRow: (row, context, trackers) => {
      const errors = [];
      const { models: existingData } = context;
      const areaId = row["Area ID"] ? Number(row["Area ID"]) : null;
      const areaName = row["Area Name"] ? String(row["Area Name"]).trim().toLowerCase() : null;

      if (areaId !== null) {
        if (trackers.ids.has(areaId)) errors.push("Duplicate Area ID");
        else trackers.ids.add(areaId);

        if (!existingData.some((m) => m.areaId === areaId)) errors.push("Area ID does not exist");
      }

      if (areaName) {
        const dbMatch = existingData.find((m) => m.areaName.trim().toLowerCase() === areaName);
        if (dbMatch && dbMatch.areaId !== areaId) errors.push("Area Name already exists in DB");

        if (trackers.names.has(areaName)) errors.push("Duplicate Area Name in file");
        else trackers.names.add(areaName);
      }

      if (row["Latitude"] && isNaN(Number(row["Latitude"])))
        errors.push("Latitude must be a number");
      if (row["Longitude"] && isNaN(Number(row["Longitude"])))
        errors.push("Longitude must be a number");

      return errors;
    },
    mapToDTO: (row, context) => ({
      areaId: row["Area ID"] ? Number(row["Area ID"]) : null,
      areaName: row["Area Name"]?.trim() || "",
      longitude: row["Longitude"] ? parseFloat(row["Longitude"]) : null,
      latitude: row["Latitude"] ? parseFloat(row["Latitude"]) : null,
    }),
  },

  // =========================
  // 2. BRANCHES
  // =========================
  branches: {
    requiredFields: ["Branch Name", "Area Name", "Latitude", "Longitude"],
    initTrackers: () => ({ ids: new Set(), names: new Set() }),

    getColumns: (contextData) => [
      { field: "Branch ID", headerName: "ID", width: 90, editable: true, type: "number" },
      { field: "Branch Name", headerName: "Name", flex: 1, editable: true },
      {
        field: "Area Name",
        headerName: "Area",
        width: 200,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(
            p,
            contextData.areas?.map((a) => a.areaName)
          ),
      },
      { field: "Latitude", headerName: "Lat", width: 120, editable: true },
      { field: "Longitude", headerName: "Lon", width: 120, editable: true },
    ],

    validateRow: (row, context, trackers) => {
      const errors = [];
      const { models: existingData } = context;
      const branchId = row["Branch ID"] ? Number(row["Branch ID"]) : null;
      const branchName = row["Branch Name"]
        ? String(row["Branch Name"]).trim().toLowerCase()
        : null;

      if (branchId !== null) {
        if (trackers.ids.has(branchId)) errors.push("Duplicate Branch ID");
        else trackers.ids.add(branchId);

        if (!existingData.some((b) => b.branchId === branchId))
          errors.push("Branch ID does not exist");
      }

      if (branchName) {
        const dbMatch = existingData.find((b) => b.branchName.trim().toLowerCase() === branchName);
        if (dbMatch && dbMatch.branchId !== branchId) errors.push("Branch Name already exists");

        if (trackers.names.has(branchName)) errors.push("Duplicate Branch Name");
        else trackers.names.add(branchName);
      }

      if (
        row["Area Name"] &&
        !context.areas.some(
          (a) => a.areaName.toLowerCase() === String(row["Area Name"]).toLowerCase()
        )
      ) {
        errors.push("Area Name Doesn't Exist");
      }

      return errors;
    },
    mapToDTO: (row, context) => ({
      branchId: row["Branch ID"] ? Number(row["Branch ID"]) : null,
      branchName: row["Branch Name"]?.trim() || "",
      // Lookup Area ID from Area Name
      areaId:
        context.areas?.find(
          (a) => a.areaName.toLowerCase() === String(row["Area Name"]).toLowerCase()
        )?.areaId || null,
      longitude: row["Longitude"] ? parseFloat(row["Longitude"]) : null,
      latitude: row["Latitude"] ? parseFloat(row["Latitude"]) : null,
      companyId: context.companyId,
    }),
  },

  // =========================
  // 3. JOBS
  // =========================
  jobs: {
    requiredFields: ["Job Title", "Department Name"],
    initTrackers: () => ({ ids: new Set(), names: new Set() }),

    getColumns: (contextData, rows) => [
      { field: "Job ID", headerName: "Job ID", width: 90, editable: true },
      { field: "Job Title", headerName: "Job Title", flex: 1, editable: true },
      {
        field: "Department Name",
        headerName: "Department Name",
        width: 200,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(
            p,
            contextData.departments?.map((d) => d.departmentName)
          ),
      },
      {
        field: "Reporting To Solid",
        headerName: "Reporting To Solid",
        width: 200,
        editable: true,
        renderEditCell: (params) => {
          // Logic: Merge DB Jobs + New CSV Jobs (that don't have ID yet)
          const dbJobs = contextData.models?.map((j) => j.jobName) || [];
          const newJobs =
            rows
              ?.filter((r) => (!r["Job ID"] || r["Job ID"] === "") && r["Job Title"])
              .map((r) => r["Job Title"]) || [];

          const uniqueOptions = Array.from(new Set([...dbJobs, ...newJobs]));
          return RenderDropdown(params, uniqueOptions);
        },
      }, // --- NEW: DOTTED MANAGER ---
      {
        field: "Reporting To Dotted",
        headerName: "Reporting To Dotted",
        width: 200,
        editable: true,
        renderEditCell: (params) => {
          const dbJobs = contextData.models?.map((j) => j.jobName) || [];
          // Include jobs being added in this file
          const newJobs = rows?.filter((r) => r["Job Title"]).map((r) => r["Job Title"]) || [];
          const uniqueOptions = Array.from(new Set([...dbJobs, ...newJobs]));
          return RenderDropdown(params, uniqueOptions);
        },
      },
    ],

    validateRow: (row, context, trackers, allRows) => {
      const errors = [];
      const { models: existingData } = context;
      const jobId = row["Job ID"] ? Number(row["Job ID"]) : null;
      const jobName = row["Job Title"] ? String(row["Job Title"]).trim().toLowerCase() : null;

      if (jobId !== null) {
        if (trackers.ids.has(jobId)) errors.push("Duplicate Job ID");
        else trackers.ids.add(jobId);

        if (!existingData.some((j) => j.jobId === jobId)) errors.push("Job ID does not exist");
      }

      if (jobName) {
        const dbMatch = existingData.find((j) => j.jobName.trim().toLowerCase() === jobName);
        if (dbMatch && dbMatch.jobId !== jobId) errors.push("Job Title already exists");

        if (trackers.names.has(jobName)) errors.push("Duplicate Job Title");
        else trackers.names.add(jobName);
      }

      if (
        row["Department Name"] &&
        !context.departments?.some(
          (d) => d.departmentName.toLowerCase() === String(row["Department Name"]).toLowerCase()
        )
      ) {
        errors.push("Department Name doesn't exist");
      }

      // Reporting To Logic
      const solid = row["Reporting To Solid"]
        ? String(row["Reporting To Solid"]).trim().toLowerCase()
        : null;
      if (solid) {
        if (solid) {
          // 1. Check if reporting to self
          if (solid === jobName) {
            errors.push("Cannot report to self");
          } else {
            // 2. Check DB
            const inDb = existingData?.some((j) => j.jobName.toLowerCase() === solid);
            // 3. Check File
            const inFile = allRows?.some(
              (r) => String(r["Job Title"]).trim().toLowerCase() === solid
            );

            if (!inDb && !inFile) {
              errors.push("Solid Manager not found (in DB or File)");
            }
          }
        }
      }

      const dotted = row["Reporting To Dotted"]
        ? String(row["Reporting To Dotted"]).trim().toLowerCase()
        : null;
      if (dotted) {
        if (dotted === jobName) {
          errors.push("Cannot report to self");
        } else {
          const inDb = existingData?.some((j) => j.jobName.toLowerCase() === dotted);
          const inFile = allRows?.some(
            (r) => String(r["Job Title"]).trim().toLowerCase() === dotted
          );

          if (!inDb && !inFile) {
            errors.push("Dotted Manager not found");
          }
        }
      }

      return errors;
    },
    mapToDTO: (row, context) => {
      // Helper to find ID from Name
      const findJobId = (name) => {
        if (!name) return null;
        return (
          context.models?.find(
            (j) => j.jobName.toLowerCase().trim() === String(name).toLowerCase().trim()
          )?.jobId || null
        );
      };
      return {
        jobId: row["Job ID"] ? Number(row["Job ID"]) : null,
        jobName: row["Job Title"]?.trim() || "",
        departmentId:
          context.departments?.find(
            (d) =>
              d.departmentName.toLowerCase().trim() ===
              String(row["Department Name"]).toLowerCase().trim()
          )?.departmentId || null,

        // Solid Line
        reportingToSolidLine: String(row["Reporting To Solid"]) || null,
        reportingToSolidId: findJobId(row["Reporting To Solid"]), // Try to resolve ID

        // New: Dotted Line
        reportingToDottedLine: String(row["Reporting To Dotted"]) || null,
        reportingToDottedId: findJobId(row["Reporting To Dotted"]), // Try to resolve ID

        companyId: context.companyId,
      };
    },
  },

  // =========================
  // 4. DEPARTMENTS
  // =========================
  departments: {
    requiredFields: ["Department Name"],
    initTrackers: () => ({ ids: new Set(), names: new Set() }),

    getColumns: () => [
      { field: "Department ID", headerName: "ID", width: 90, editable: true },
      { field: "Department Name", headerName: "Name", flex: 1, editable: true },
    ],

    validateRow: (row, context, trackers) => {
      const errors = [];
      const { models: existingData } = context;
      const id = row["Department ID"] ? Number(row["Department ID"]) : null;
      const name = row["Department Name"] ? row["Department Name"].trim().toLowerCase() : null;

      if (id !== null) {
        if (trackers.ids.has(id)) errors.push("Duplicate Dept ID");
        else trackers.ids.add(id);
        if (!existingData.some((d) => d.departmentId === id)) errors.push("Dept ID does not exist");
      }

      if (name) {
        const dbMatch = existingData.find((d) => d.departmentName.trim().toLowerCase() === name);
        if (dbMatch && dbMatch.departmentId !== id) errors.push("Dept Name already exists");

        if (trackers.names.has(name)) errors.push("Duplicate Dept Name");
        else trackers.names.add(name);
      }
      return errors;
    },
    mapToDTO: (row, context) => ({
      departmentId: row["Department ID"] ? Number(row["Department ID"]) : null,
      departmentName: row["Department Name"]?.trim() || "",
      companyId: context.companyId,
    }),
  },

  // =========================
  // 5. EMPLOYEES
  // =========================
  employees: {
    requiredFields: ["Name", "Email", "Role", "Branch", "Department", "Job", "Gender"],
    initTrackers: () => ({ ids: new Set(), names: new Set(), emails: new Set() }),

    getColumns: (contextData, rows) => [
      { field: "ID", headerName: "ID", width: 80, editable: true },
      { field: "Name", headerName: "Name", width: 150, editable: true },
      { field: "Email", headerName: "Email", width: 200, editable: true },
      {
        field: "Role",
        headerName: "Role",
        width: 100,
        editable: true,
        renderEditCell: (p) => RenderDropdown(p, ["admin", "user"]),
      },
      {
        field: "Gender",
        headerName: "Gender",
        width: 100,
        editable: true,
        renderEditCell: (p) => RenderDropdown(p, ["Male", "Female"]),
      },
      {
        field: "Branch",
        headerName: "Branch",
        width: 150,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(
            p,
            contextData.branches?.map((b) => b.branchName)
          ),
      },
      {
        field: "Department",
        headerName: "Department",
        width: 150,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(
            p,
            contextData.departments?.map((d) => d.departmentName)
          ),
      },
      {
        // JOB DEPENDS ON DEPARTMENT
        field: "Job",
        headerName: "Job",
        width: 150,
        editable: true,
        renderEditCell: (params) => {
          const { id, field, api, row } = params;
          const currentDept = row["Department"]
            ? String(row["Department"]).trim().toLowerCase()
            : null;

          // Filter Jobs by Department
          const filteredJobs = currentDept
            ? contextData.jobs
                ?.filter((j) => {
                  const jobDept = contextData.departments?.find(
                    (d) => d.departmentId === j.departmentId
                  );
                  return jobDept?.departmentName.trim().toLowerCase() === currentDept;
                })
                .map((j) => j.jobName) || []
            : [];

          return RenderDropdown(params, filteredJobs);
        },
      },
      {
        // DIRECT MANAGER (Updates Indirect Manager)
        field: "Direct Manager",
        headerName: "Direct Manager",
        width: 160,
        editable: true,
        renderEditCell: (params) => {
          const { id, field, api, row, value } = params;

          // Options: DB Employees + New Rows (excluding self)
          const dbEmps = contextData.models || [];
          const csvEmps = rows.filter((r) => !r["ID"]); // New rows usually lack ID initially

          const allOptions = [...dbEmps, ...csvEmps]
            .filter((e) => {
              const eName = e.name || e["Name"];
              const rName = row["Name"];
              return eName && String(eName).trim() !== String(rName).trim();
            })
            .map((e) => ({
              label: e.name || e["Name"],
              value: e.name || e["Name"],
              directManager: e.directManagerId || e["Direct Manager"],
            }));

          return (
            <Autocomplete
              fullWidth
              options={allOptions}
              getOptionLabel={(o) => o.label || ""}
              value={allOptions.find((o) => o.value === value) || null}
              renderInput={(p) => <TextField {...p} size="small" />}
              onChange={(_, newValue) => {
                const managerName = newValue?.value || "";
                api.setEditCellValue({ id, field, value: managerName });

                // Logic to auto-set Indirect Manager
                if (newValue?.directManager) {
                  // Find name of the indirect manager (ID or Name)
                  let indirectName = newValue.directManager;
                  // If it's an ID, find the name
                  const indirectObj = [...dbEmps, ...csvEmps].find(
                    (e) =>
                      e.id == indirectName || e.name === indirectName || e["Name"] === indirectName
                  );
                  if (indirectObj) indirectName = indirectObj.name || indirectObj["Name"];

                  // Update Row
                  const updatedRow = { ...api.getRow(id), "Indirect Manager": indirectName };
                  api.updateRows([updatedRow]);
                } else {
                  api.setEditCellValue({ id, field: "Indirect Manager", value: null });
                }
              }}
            />
          );
        },
      },
      {
        field: "Indirect Manager",
        headerName: "Indirect Manager",
        width: 160,
        editable: false,
        renderCell: (p) => <div style={{ opacity: 0.6 }}>{p.value || "-"}</div>,
      },
      { field: "Phone Number", headerName: "Phone Number", width: 130, editable: true },
    ],

    validateRow: (row, context, trackers, allRows) => {
      const errors = [];
      const { models: existingData, branches, jobs } = context;

      const id = row["ID"];
      const name = row["Name"] ? String(row["Name"]).trim() : null;
      const email = row["Email"] ? String(row["Email"]).trim().toLowerCase() : null;

      // Check ID
      if (id) {
        if (trackers.ids.has(id)) errors.push("Duplicate ID");
        else trackers.ids.add(id);
        if (!existingData.some((e) => e.id === id)) errors.push("ID does not exist");
      }

      // Check Name
      if (name) {
        if (trackers.names.has(name.toLowerCase())) errors.push("Duplicate Name");
        else trackers.names.add(name.toLowerCase());
        // Check DB excluding self
        if (
          existingData.some(
            (e) => e.name?.trim().toLowerCase() === name.toLowerCase() && e.id !== id
          )
        ) {
          errors.push("Name already exists in DB");
        }
      }

      // Check Email
      if (email) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push("Invalid Email");
        if (trackers.emails.has(email)) errors.push("Duplicate Email");
        else trackers.emails.add(email);

        if (existingData.some((e) => e.email === email && e.id !== id))
          errors.push("Email exists in DB");
      }

      // Check Branch
      if (
        row["Branch"] &&
        !branches.some((b) => b.branchName.toLowerCase() === row["Branch"].toLowerCase())
      ) {
        errors.push("Branch doesn't exist");
      }

      // Check Job
      if (row["Job"] && row["Department"]) {
        const foundJob = jobs.find(
          (j) => j.jobName.trim().toLowerCase() === row["Job"].toLowerCase()
        );
        // Note: Logic needs exact department matching
        if (!foundJob) errors.push("Job doesn't exist");
        // else if (foundJob.departmentId !== ...) errors.push("Job mismatch");
      }

      const directManager = row["Direct Manager"]
        ? String(row["Direct Manager"]).trim().toLowerCase()
        : null;

      if (directManager) {
        // 1. Self-reporting check
        if (name && name.toLowerCase() === directManager) {
          errors.push("Cannot report to self");
        } else {
          // 2. Check Database
          const inDb = existingData.some((e) => e.name?.trim().toLowerCase() === directManager);

          // 3. Check Current File (Look ahead/behind in the excel sheet)
          const inFile = allRows.some(
            (r) => String(r["Name"]).trim().toLowerCase() === directManager
          );

          if (!inDb && !inFile) {
            errors.push("Direct Manager not found (in DB or File)");
          }
        }
      }

      return errors;
    },
    mapToDTO: (row, context) => ({
      id: row["ID"] || null,
      name: String(row["Name"])?.trim() || "",
      email: String(row["Email"])?.trim() || "",
      phoneNumber: row["Phone Number"]?.toString().trim() || "",
      referencedEmployeeId: row["Referenced ID"] ? Number(row["Referenced ID"]) : null,
      role: row["Role"] === "Admin" ? 1 : row["Role"] === "User" ? 2 : 3,
      gender: row["Gender"]?.trim() || "",
      branchId:
        context.branches?.find(
          (b) => b.branchName.toLowerCase() === String(row["Branch"]).toLowerCase()
        )?.branchId || null,
      departmentId:
        context.departments?.find(
          (d) => d.departmentName.toLowerCase() === String(row["Department"]).toLowerCase()
        )?.departmentId || null,
      jobId:
        context.jobs?.find((j) => j.jobName.toLowerCase() === String(row["Job"]).toLowerCase())
          ?.jobId || null,
      directLineManager: row["Direct Manager"] || null,
      companyId: context.companyId,
    }),
  },

  // =========================
  // 6. KPIS
  // =========================
  kpis: {
    requiredFields: ["KPI Name", "Perspective", "KPI Formula"],
    initTrackers: () => ({ ids: new Set(), names: new Set() }),

    getColumns: () => [
      { field: "KPI ID", headerName: "ID", width: 90, editable: true },
      { field: "KPI Name", headerName: "Name", flex: 1, editable: true },
      {
        field: "Perspective",
        headerName: "Perspective",
        width: 200,
        editable: true,
        renderEditCell: (p) => RenderDropdown(p, PERSPECTIVES),
      },
      {
        field: "KPI Formula",
        headerName: "Formula",
        width: 150,
        editable: true,
        renderEditCell: (p) => RenderDropdown(p, VALID_FORMULAS),
      },
    ],

    validateRow: (row, context, trackers) => {
      const errors = [];
      const { models: existingData } = context;
      const id = row["KPI ID"] ? Number(row["KPI ID"]) : null;
      const name = row["KPI Name"] ? String(row["KPI Name"]).trim().toLowerCase() : null;

      if (id !== null) {
        if (trackers.ids.has(id)) errors.push("Duplicate ID");
        else trackers.ids.add(id);
        if (!existingData.some((k) => k.kpiNameId === id)) errors.push("ID does not exist");
      }

      if (name) {
        if (trackers.names.has(name)) errors.push("Duplicate Name");
        else trackers.names.add(name);
        const dbMatch = existingData.find((k) => k.kpiName.trim().toLowerCase() === name);
        if (dbMatch && dbMatch.kpiNameId !== id) errors.push("Name exists in DB");
      }

      if (row["Perspective"] && !PERSPECTIVES.includes(row["Perspective"]))
        errors.push("Invalid Perspective");
      if (row["KPI Formula"] && !VALID_FORMULAS.includes(row["KPI Formula"].toLowerCase()))
        errors.push("Invalid Formula");

      return errors;
    },
    mapToDTO: (row, context) => ({
      kpiNameId: row["KPI ID"] ? Number(row["KPI ID"]) : null,
      kpiName: String(row["KPI Name"])?.trim() || "",
      perspectiveId: PERSPECTIVES.indexOf(String(row["Perspective"])) + 1,
      kpiFormula: String(row["KPI Formula"])?.trim() || "Regular",
      companyId: context.companyId,
    }),
  },

  // =========================
  // 7. SCORECARDS
  // =========================
  scorecards: {
    requiredFields: [
      "Job",
      "Branch",
      "KPI",
      "Weight",
      "Target",
      "Frequency",
      "Starting Date",
      "Ending Date",
    ],
    initTrackers: () => ({}), // No duplicate tracking needed per row usually

    getColumns: (contextData) => [
      {
        field: "Job",
        headerName: "Job",
        width: 150,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(
            p,
            contextData.jobs?.map((j) => j.jobName)
          ),
      },
      {
        field: "Branch",
        headerName: "Branch",
        width: 150,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(
            p,
            contextData.branches?.map((b) => b.branchName)
          ),
      },
      {
        field: "KPI",
        headerName: "KPI",
        width: 200,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(
            p,
            contextData.kpiNames?.map((k) => k.kpiName)
          ), // 'models' are assignments/kpis here
      },
      {
        field: "Primary Assessor",
        headerName: "Primary Assessor",
        width: 150,
        editable: true,
        renderEditCell: (p) => RenderDropdown(p, contextData.employees?.map((e) => e.name) || []), // Assuming employees loaded
      },
      { field: "Weight", headerName: "Weight", width: 90, type: "number", editable: true },
      { field: "Target", headerName: "Target", width: 90, type: "number", editable: true },
      {
        field: "Frequency",
        headerName: "Frequency",
        width: 120,
        editable: true,
        renderEditCell: (p) => RenderDropdown(p, FREQUENCY_OPTIONS),
      },
      {
        field: "Accountability",
        headerName: "Accountability",
        width: 120,
        editable: true,
        renderEditCell: (p) => RenderDropdown(p, ACCOUNTABILITIES),
      },
      { field: "Starting Date", headerName: "Start", width: 120, editable: true },
      { field: "Ending Date", headerName: "End", width: 120, editable: true },
    ],

    validateRow: (row, context) => {
      const errors = [];
      const { jobs, branches, kpiNames } = context;
      if (row["Job"] && !jobs.some((j) => j.jobName.trim() === row["Job"].trim()))
        errors.push("Job not found");
      if (row["Branch"] && !branches.some((b) => b.branchName.trim() === row["Branch"].trim()))
        errors.push("Branch not found");

      if (row["KPI"] && !kpiNames.some((k) => k.kpiName.trim() === row["KPI"].trim()))
        errors.push("KPI not found");

      if (row["Weight"] && isNaN(Number(row["Weight"]))) errors.push("Weight must be number");
      if (row["Target"] && isNaN(Number(row["Target"]))) errors.push("Target must be number");

      if (row["Frequency"] && !FREQUENCY_OPTIONS.includes(row["Frequency"]))
        errors.push("Invalid Frequency");
      if (row["Accountability"] && !ACCOUNTABILITIES.includes(row["Accountability"]))
        errors.push("Invalid Accountability");

      // Date Validation
      const dateRegex = /^(0?[1-9]|[12][0-9]|3[01])[\/\-](0?[1-9]|1[0-2])[\/\-]\d{4}$/;
      if (row["Starting Date"] && !dateRegex.test(row["Starting Date"]))
        errors.push("Invalid Start Date");
      if (row["Ending Date"] && !dateRegex.test(row["Ending Date"]))
        errors.push("Invalid End Date");
      return errors;
    },
    mapToDTO: (row, context) => {
      const kpi = context.kpiNames?.find((k) => k.kpiName.trim() === String(row["KPI"]).trim());
      const job = context.jobs?.find((j) => j.jobName.trim() === String(row["Job"]).trim());
      const branch = context.branches?.find(
        (b) => b.branchName.trim() === String(row["Branch"]).trim()
      );
      // Note: context.employees needed here for Assessors

      return {
        kpiNameId: kpi?.kpiNameId,
        jobId: job?.jobId,
        branchId: branch?.branchId,
        // primaryAssessorId: ... (Lookup from context.employees),
        weight: Number(row["Weight"]),
        target: Number(row["Target"]),
        frequency: row["Frequency"] || "Monthly",
        accountability: row["Accountability"] || "Prime",
        from: parseDate(row["Starting Date"]),
        to: row["Ending Date"] ? parseDate(row["Ending Date"]) : null,
      };
    },
  },

  // =========================
  // 8. JOB DETAILS
  // =========================
  jobDetails: {
    // 1. Match Excel Header exactly ("Job Name", not "job_id")
    requiredFields: ["Job Name"],
    initTrackers: () => ({ jobs: new Set() }),

    getColumns: (contextData) => [
      // --- The Lookup Field ---
      {
        field: "Job Name",
        headerName: "Job Name",
        width: 250,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(p, contextData.jobs?.map((j) => j.jobName).filter(Boolean)),
      },
      // --- The Rest (Title Case keys to match Excel) ---
      { field: "Job Code", headerName: "Job Code", width: 150, editable: true },
      { field: "Title", headerName: "Title", width: 200, editable: true },
      { field: "Skill Pool", headerName: "Skill Pool", width: 150, editable: true },
      {
        field: "Reference Indicator",
        headerName: "Reference Indicator",
        width: 180,
        editable: true,
      },
      { field: "Authorized Deputy", headerName: "Authorized Deputy", width: 180, editable: true },
      { field: "Major Amendments", headerName: "Major Amendments", width: 200, editable: true },
      { field: "Commentary", headerName: "Commentary", width: 200, editable: true },
      {
        field: "Job Description Modification",
        headerName: "Job Description Modification",
        width: 220,
        editable: true,
      },
      { field: "Review Date", headerName: "Review Date", width: 150, editable: true },
      {
        field: "Prior Transformation Phase",
        headerName: "Prior Transformation Phase",
        width: 220,
        editable: true,
      },
      {
        field: "Post Transformation Phase",
        headerName: "Post Transformation Phase",
        width: 220,
        editable: true,
      },
      {
        field: "Job Working Schedule",
        headerName: "Job Working Schedule",
        width: 180,
        editable: true,
      },
      { field: "Job Location", headerName: "Job Location", width: 150, editable: true },
      { field: "Job Mobility", headerName: "Job Mobility", width: 150, editable: true },
      { field: "TCOE", headerName: "TCOE", width: 100, editable: true },
      {
        field: "Number Of National Staff",
        headerName: "Number Of National Staff",
        width: 200,
        editable: true,
      },
      {
        field: "Number Of Expatriate Staff",
        headerName: "Number Of Expatriate Staff",
        width: 200,
        editable: true,
      },
      { field: "Annual Total Cost", headerName: "Annual Total Cost", width: 180, editable: true },
      {
        field: "Other Key Statistics",
        headerName: "Other Key Statistics",
        width: 200,
        editable: true,
      },
      {
        field: "Annual Training Budget",
        headerName: "Annual Training Budget",
        width: 200,
        editable: true,
      },
      { field: "JG Evaluation Date", headerName: "JG Evaluation Date", width: 180, editable: true },
      { field: "Job Purpose", headerName: "Job Purpose", width: 300, editable: true },
      {
        field: "Key Responsibilities",
        headerName: "Key Responsibilities",
        width: 300,
        editable: true,
      },
      { field: "Education", headerName: "Education", width: 200, editable: true },
      { field: "Experience", headerName: "Experience", width: 200, editable: true },
      { field: "Languages", headerName: "Languages", width: 150, editable: true },
      {
        field: "Information Technology",
        headerName: "Information Technology",
        width: 200,
        editable: true,
      },
      {
        field: "Physical Requirements",
        headerName: "Physical Requirements",
        width: 200,
        editable: true,
      },
      {
        field: "Mental Emotional Requirements",
        headerName: "Mental Emotional Requirements",
        width: 220,
        editable: true,
      },
      { field: "Equipment", headerName: "Equipment", width: 150, editable: true },
      { field: "Indoor", headerName: "Indoor", width: 120, editable: true },
      { field: "Outdoor", headerName: "Outdoor", width: 120, editable: true },
      { field: "Working Hazard", headerName: "Working Hazard", width: 180, editable: true },
      { field: "Working Days", headerName: "Working Days", width: 150, editable: true },
      { field: "Days Off", headerName: "Days Off", width: 150, editable: true },
      { field: "Working Hours", headerName: "Working Hours", width: 150, editable: true },
      {
        field: "Internal Relationships",
        headerName: "Internal Relationships",
        width: 200,
        editable: true,
      },
      {
        field: "External Relationships",
        headerName: "External Relationships",
        width: 200,
        editable: true,
      },
      { field: "Military Status", headerName: "Military Status", width: 150, editable: true },
      { field: "Job Values", headerName: "Job Values", width: 200, editable: true },
      {
        field: "Strengthening Leadership Capabilities",
        headerName: "Strengthening Leadership Capabilities",
        width: 250,
        editable: true,
      },
      { field: "Process Mapping", headerName: "Process Mapping", width: 180, editable: true },
      { field: "EVP Refinement", headerName: "EVP Refinement", width: 180, editable: true },
      {
        field: "Top Performing Behaviours",
        headerName: "Top Performing Behaviours",
        width: 220,
        editable: true,
      },
      { field: "General Terms", headerName: "General Terms", width: 200, editable: true },
      { field: "Data Integrity", headerName: "Data Integrity", width: 180, editable: true },
      { field: "Document Name", headerName: "Document Name", width: 180, editable: true },
      {
        field: "Document Prepared By",
        headerName: "Document Prepared By",
        width: 200,
        editable: true,
      },
      { field: "Document Owner", headerName: "Document Owner", width: 200, editable: true },
    ],

    validateRow: (row, context, trackers) => {
      const errors = [];
      const { jobs } = context;

      const jobName = row["Job Name"];

      if (!jobName) {
        errors.push("Job Name is required");
      } else {
        const exists = jobs?.some(
          (j) => j.jobName.trim().toLowerCase() === String(jobName).trim().toLowerCase()
        );
        if (!exists) errors.push("Job Name not found in system");

        if (trackers.jobs.has(jobName)) errors.push("Duplicate Job Entry in file");
        else trackers.jobs.add(jobName);
      }

      return errors;
    },
    mapToDTO: (row, context) => {
      const jobObj = context.jobs?.find(
        (j) => j.jobName.trim().toLowerCase() === String(row["Job Name"]).trim().toLowerCase()
      );

      return {
        // ID
        jobId: jobObj?.jobId || null,

        // Map Excel Headers (Title Case) -> DTO Fields (Camel Case)
        jobCode: row["Job Code"] || "",
        title: row["Title"] || "",
        skillPool: row["Skill Pool"] || "",
        referenceIndicator: row["Reference Indicator"] || "",
        authorizedDeputy: row["Authorized Deputy"] || "",
        majorAmendments: row["Major Amendments"] || "",
        commentary: row["Commentary"] || "",
        jobDescriptionModification: row["Job Description Modification"] || "",
        reviewDate: row["Review Date"] || "",
        priorTransformationPhase: row["Prior Transformation Phase"] || "",
        postTransformationPhase: row["Post Transformation Phase"] || "",
        jobWorkingSchedule: row["Job Working Schedule"] || "",
        jobLocation: row["Job Location"] || "",
        jobMobility: row["Job Mobility"] || "",
        tcoe: row["TCOE"] || "",
        numberOfNationalStaff: row["Number Of National Staff"] || "",
        numberOfExpatriateStaff: row["Number Of Expatriate Staff"] || "",
        annualTotalCost: row["Annual Total Cost"] || "",
        otherKeyStatistics: row["Other Key Statistics"] || "",
        annualTrainingBudget: row["Annual Training Budget"] || "",
        jgEvaluationDate: row["JG Evaluation Date"] || "",
        jobPurpose: row["Job Purpose"] || "",
        keyResponsibilities: row["Key Responsibilities"] || "",
        education: row["Education"] || "",
        experience: row["Experience"] || "",
        languages: row["Languages"] || "",
        informationTechnology: row["Information Technology"] || "",
        physicalRequirements: row["Physical Requirements"] || "",
        mentalEmotionalRequirements: row["Mental Emotional Requirements"] || "",
        equipment: row["Equipment"] || "",
        indoor: row["Indoor"] || "",
        outdoor: row["Outdoor"] || "",
        workingHazard: row["Working Hazard"] || "",
        workingDays: row["Working Days"] || "",
        daysOff: row["Days Off"] || "",
        workingHours: row["Working Hours"] || "",
        internalRelationships: row["Internal Relationships"] || "",
        externalRelationships: row["External Relationships"] || "",
        militaryStatus: row["Military Status"] || "",
        jobValues: row["Job Values"] || "",
        strengtheningLeadershipCapabilities: row["Strengthening Leadership Capabilities"] || "",
        processMapping: row["Process Mapping"] || "",
        evpRefinement: row["EVP Refinement"] || "",
        topPerformingBehaviours: row["Top Performing Behaviours"] || "",
        generalTerms: row["General Terms"] || "",
        dataIntegrity: row["Data Integrity"] || "",
        documentName: row["Document Name"] || "",
        documentPreparedBy: row["Document Prepared By"] || "",
        documentOwner: row["Document Owner"] || "",
      };
    },
  },

  // =========================
  // 8. COMPETENCIES
  // =========================

  competencies: {
    requiredFields: ["Competency Name", "Criteria"],
    initTrackers: () => ({ ids: new Set(), names: new Set() }),

    getColumns: () => [
      { field: "Competency ID", headerName: "ID", width: 90, editable: true, type: "number" },
      { field: "Competency Name", headerName: "Name", flex: 1, editable: true },
      { field: "Definition", headerName: "Definition", flex: 1, editable: true },
      {
        field: "Criteria",
        headerName: "Criteria",
        width: 150,
        editable: true,
        renderEditCell: (p) => RenderDropdown(p, COMPETENCY_CRITERIA),
      },
    ],

    validateRow: (row, context, trackers) => {
      const errors = [];
      const { models: existingData } = context;

      const compId = row["Competency ID"] ? Number(row["Competency ID"]) : null;
      const compName = row["Competency Name"]
        ? String(row["Competency Name"]).trim().toLowerCase()
        : null;
      const criteria = row["Criteria"] ? String(row["Criteria"]).trim() : null;

      // 1. Validate ID (Update Mode)
      if (compId !== null) {
        if (trackers.ids.has(compId)) {
          errors.push("Duplicate Competency ID in file");
        } else {
          trackers.ids.add(compId);
        }

        // Verify ID exists in the DB if user is trying to update
        const existsInDB = existingData.some((m) => m.competencyId === compId);
        if (!existsInDB) {
          errors.push("Competency ID does not exist in system");
        }
      }

      // 2. Validate Name (Required & Unique)
      if (compName) {
        if (trackers.names.has(compName)) {
          errors.push("Duplicate Competency Name in file");
        } else {
          trackers.names.add(compName);
        }

        // Check for name conflicts in DB
        const dbMatch = existingData.find((m) => m.name?.trim().toLowerCase() === compName);
        if (dbMatch && dbMatch.competencyId !== compId) {
          errors.push("Competency Name already exists in system");
        }
      } else {
        errors.push("Competency Name is required");
      }

      // 3. Validate Type (Required)
      if (!row["Criteria"] || String(row["Criteria"]).trim() === "") {
        errors.push("Criteria is required");
      }

      if (!criteria) {
        errors.push("Criteria is required");
      } else if (!COMPETENCY_CRITERIA.includes(criteria)) {
        // Strict check: "core" will fail, "Core" will pass
        errors.push("Invalid Criteria. Must be exactly: Core, Technical, or Leadership");
      }

      return errors;
    },

    mapToDTO: (row, context) => ({
      competencyId: row["Competency ID"] ? Number(row["Competency ID"]) : null,
      name: row["Competency Name"]?.trim() || "",
      type: row["Criteria"]?.trim() || "",
      definition: row["Definition"]?.trim() || "",
      // companyId: context.companyId, // Include if your backend requires it
    }),
  },

  // =========================
  // 9. COMPETENCY LEVELS
  // =========================
  competencyLevels: {
    requiredFields: ["Competency Level Name", "Value", "Start Date", "Competency Name"],
    initTrackers: () => ({ ids: new Set() }),

    getColumns: (contextData) => [
      { field: "Competency Level ID", headerName: "ID", width: 90, editable: true, type: "number" },
      { field: "Competency Level Name", headerName: "Level Name", flex: 1, editable: true },
      { field: "Definition", headerName: "Definition", flex: 1, editable: true },
      { field: "Value", headerName: "Value", width: 90, type: "number", editable: true },
      { field: "Start Date", headerName: "Start Date", width: 120, editable: true },
      { field: "End Date", headerName: "End Date", width: 120, editable: true },
      {
        field: "Competency Name",
        headerName: "Competency",
        width: 200,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(
            p,
            contextData.competencies?.map((c) => c.name) // Using 'name' from backend fields
          ),
      },
    ],

    validateRow: (row, context, trackers) => {
      const errors = [];
      const { models: existingLevels, competencies } = context;

      const levelId = row["Competency Level ID"] ? Number(row["Competency Level ID"]) : null;
      const levelName = row["Competency Level Name"]?.trim();
      const value = row["Value"];
      const compName = row["Competency Name"]?.trim();

      // 1. Validate ID (Update Mode)
      if (levelId !== null) {
        if (trackers.ids.has(levelId)) errors.push("Duplicate Level ID in file");
        else trackers.ids.add(levelId);

        if (!existingLevels.some((l) => l.competencyLevelId === levelId)) {
          errors.push("Competency Level ID does not exist");
        }
      }

      // 2. Validate Name & Value
      if (!levelName) errors.push("Level Name is required");
      if (value === undefined || value === "" || isNaN(Number(value))) {
        errors.push("Value must be a number");
      }

      // 3. Validate Competency Link (Lookup)
      if (!compName) {
        errors.push("Competency Name is required");
      } else if (!competencies.some((c) => c.name.trim() === compName)) {
        errors.push(`Competency '${compName}' not found in system`);
      }

      // 4. Date Validation (Matching Java dd/MM/yyyy)
      const dateRegex = /^(0?[1-9]|[12][0-9]|3[01])[\/\-](0?[1-9]|1[0-2])[\/\-]\d{4}$/;

      if (row["Start Date"] && !dateRegex.test(row["Start Date"])) {
        errors.push("Start Date must be dd/MM/yyyy");
      }

      if (row["End Date"] && !dateRegex.test(row["End Date"])) {
        errors.push("End Date must be dd/MM/yyyy");
      }

      return errors;
    },

    mapToDTO: (row, context) => {
      // Find the parent Competency ID based on the Name provided in Excel
      const parentComp = context.competencies?.find(
        (c) => c.name.trim() === String(row["Competency Name"]).trim()
      );

      return {
        competencyLevelId: row["Competency Level ID"] ? Number(row["Competency Level ID"]) : null,
        levelName: row["Competency Level Name"]?.trim() || "",
        definition: row["Definition"]?.trim() || "",
        value: Number(row["Value"]),
        startDate: parseDate(row["Start Date"]), // Uses your helper to convert to YYYY-MM-DD
        endDate: row["End Date"] ? parseDate(row["End Date"]) : null,
        competencyId: parentComp?.competencyId || null,
      };
    },
  },

  // =========================
  // 10. COMPETENCIES TO JOB
  // =========================
  competencyToJob: {
    requiredFields: ["Competency Name", "Job Name", "Competency Level Name"],
    initTrackers: () => ({ ids: new Set() }),

    getColumns: (contextData) => [
      {
        field: "Competency To Job ID",
        headerName: "ID",
        width: 90,
        editable: true,
        type: "number",
      },
      {
        field: "Competency Name",
        headerName: "Competency",
        width: 180,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(p, contextData.competencies?.map((c) => c.name).filter(Boolean)),
      },
      {
        field: "Job Name",
        headerName: "Job Name",
        width: 180,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(p, contextData.jobs?.map((j) => j.jobName).filter(Boolean)),
      },

      // --- FIXED JOB GROUP DROPDOWN ---
      {
        field: "Job Group Name",
        headerName: "Job Group",
        width: 180,
        editable: true,
        renderEditCell: (params) => {
          const { row } = params;
          const selectedJobName = row["Job Name"];
          let filteredGroups = [];

          if (selectedJobName && contextData.jobs && contextData.jobGroups) {
            // 1. Find Job ID safely
            const jobObj = contextData.jobs.find(
              (j) =>
                j.jobName?.trim().toLowerCase() === String(selectedJobName).trim().toLowerCase()
            );

            // 2. Filter Groups safely
            if (jobObj) {
              filteredGroups = contextData.jobGroups
                .filter((g) => g.jobId === jobObj.jobId)
                // Handle potential property name mismatch (jobGroupName vs jobGroup)
                .map((g) => g.jobGroupName || g.jobGroup)
                .filter(Boolean);
            }
          }

          return RenderDropdown(params, filteredGroups);
        },
      },

      // --- FIXED REQUIRED LEVEL DROPDOWN ---
      {
        field: "Competency Level Name",
        headerName: "Required Level",
        width: 180,
        editable: true,
        renderEditCell: (params) => {
          const { row } = params;
          const selectedCompName = row["Competency Name"];
          let filteredLevels = [];

          if (selectedCompName && contextData.competencies && contextData.competencyLevels) {
            // 1. Find Competency ID safely
            const compObj = contextData.competencies.find(
              (c) => c.name?.trim().toLowerCase() === String(selectedCompName).trim().toLowerCase()
            );

            // 2. Filter Levels safely
            if (compObj) {
              filteredLevels = contextData.competencyLevels
                .filter((l) => l.competencyId === compObj.competencyId)
                .map((l) => l.levelName)
                .filter(Boolean);
            }
          }

          return RenderDropdown(params, filteredLevels);
        },
      },
    ],

    validateRow: (row, context, trackers) => {
      const errors = [];
      const {
        models: existingAssignments,
        competencies = [],
        jobs = [],
        competencyLevels = [],
        jobGroups = [],
      } = context;

      const assignmentId = row["Competency To Job ID"] ? Number(row["Competency To Job ID"]) : null;
      const compName = row["Competency Name"] ? String(row["Competency Name"]).trim() : null;
      const jobName = row["Job Name"] ? String(row["Job Name"]).trim() : null;
      const levelName = row["Competency Level Name"]
        ? String(row["Competency Level Name"]).trim()
        : null;
      const jobGroupName = row["Job Group Name"] ? String(row["Job Group Name"]).trim() : null;

      // 1. Validate ID
      if (assignmentId !== null) {
        if (trackers.ids.has(assignmentId)) errors.push("Duplicate ID in file");
        else trackers.ids.add(assignmentId);

        if (!existingAssignments?.some((a) => a.id === assignmentId)) {
          errors.push("Assignment ID does not exist");
        }
      }

      // 2. Validate Competency Existence
      if (!compName) {
        errors.push("Competency Name is required");
      } else if (
        !competencies.some((c) => c.name.trim().toLowerCase() === compName.toLowerCase())
      ) {
        errors.push(`Competency '${compName}' not found`);
      }

      // 3. Validate Job Existence
      if (!jobName) {
        errors.push("Job Name is required");
      } else if (!jobs.some((j) => j.jobName.trim().toLowerCase() === jobName.toLowerCase())) {
        errors.push(`Job '${jobName}' not found`);
      }

      // 4. Validate Level Existence & Relationship (Competency -> Level)
      if (!levelName) {
        errors.push("Level Name is required");
      } else {
        // A. Check if level exists globally
        const targetLevel = competencyLevels.find(
          (l) => l.levelName.trim().toLowerCase() === levelName.toLowerCase()
        );

        if (!targetLevel) {
          errors.push(`Level '${levelName}' not found`);
        } else if (compName) {
          // B. Check if level belongs to the selected competency
          const targetComp = competencies.find(
            (c) => c.name.trim().toLowerCase() === compName.toLowerCase()
          );

          if (targetComp && targetLevel.competencyId !== targetComp.competencyId) {
            errors.push(`Level '${levelName}' does not belong to Competency '${compName}'`);
          }
        }
      }

      // 5. Validate Job Group Relationship (Job -> Job Group)
      // Only run this if user actually provided a job group
      if (jobGroupName) {
        // Find the job object first
        const selectedJob = jobs.find(
          (j) => j.jobName.trim().toLowerCase() === jobName?.toLowerCase()
        );

        if (selectedJob) {
          // Check if this specific Group Name exists AND has the matching Job ID
          const isValidGroup = jobGroups.some(
            (g) =>
              g.jobGroup.trim().toLowerCase() === jobGroupName.toLowerCase() &&
              g.jobId === selectedJob.jobId
          );

          if (!isValidGroup) {
            errors.push(`Job Group '${jobGroupName}' does not belong to Job '${jobName}'`);
          }
        }
      }

      return errors;
    },

    mapToDTO: (row, context) => {
      const competency = context.competencies?.find(
        (c) => c.name.trim() === String(row["Competency Name"]).trim()
      );
      const job = context.jobs?.find((j) => j.jobName.trim() === String(row["Job Name"]).trim());
      const level = context.competencyLevels?.find(
        (l) => l.levelName.trim() === String(row["Competency Level Name"]).trim()
      );
      const jobGroupName = row["Job Group Name"]?.trim();
      let jobGroupId = null;
      if (jobGroupName) {
        const selectedGroup = context.jobGroups.find(
          (g) => g.jobGroup.trim() === jobGroupName && g.jobId === job?.jobId
        );
        jobGroupId = selectedGroup?.jobGroupId || null;
      }

      return {
        id: row["Competency To Job ID"] ? Number(row["Competency To Job ID"]) : null,
        jobId: job?.jobId || null,
        compLevelId: level?.competencyLevelId || null,
        jobGroupId,
      };
    },
  },

  // =========================
  // 11. COMPETENCY TO TRAINING
  // =========================
  competencyToTraining: {
    requiredFields: ["Competency Name", "Training Name"],
    initTrackers: () => ({ ids: new Set() }),

    getColumns: (contextData) => [
      {
        field: "Competency To Training ID",
        headerName: "ID",
        width: 90,
        editable: true,
        type: "number",
      },
      {
        field: "Competency Name",
        headerName: "Competency",
        width: 250,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(
            p,
            contextData.competencies?.map((c) => c.name)
          ),
      },
      {
        field: "Training Name",
        headerName: "Training Name",
        flex: 1,
        editable: true,
      },
    ],

    validateRow: (row, context, trackers) => {
      const errors = [];
      const { models: existingMappings, competencies } = context;

      const mappingId = row["Competency To Training ID"]
        ? Number(row["Competency To Training ID"])
        : null;
      const compName = row["Competency Name"]
        ? String(row["Competency Name"]).trim().toLowerCase()
        : null;
      // .trim().lowerCase() for d.b comparison
      const trainingName = row["Training Name"] ? String(row["Training Name"]).trim() : null;

      // 1. Validate ID (Update Mode)
      if (mappingId !== null) {
        if (trackers.ids.has(mappingId)) {
          errors.push("Duplicate ID in file");
        } else {
          trackers.ids.add(mappingId);
        }

        if (!existingMappings.some((m) => m.id === mappingId)) {
          errors.push("Mapping ID does not exist in the system");
        }
      }

      // 2. Validate Competency Existence
      if (!compName) {
        errors.push("Competency Name is required");
      } else if (!competencies.some((c) => c.name.trim().toLowerCase() === compName)) {
        errors.push(`Competency '${compName}' not found in system`);
      }

      // 3. Validate Training Name
      if (!trainingName) {
        errors.push("Training Name is required");
      }

      return errors;
    },

    mapToDTO: (row, context) => {
      // Find the competency object to get its ID
      const competency = context.competencies?.find(
        (c) => c.name.trim() === String(row["Competency Name"]).trim()
      );

      return {
        id: row["Competency To Training ID"] ? Number(row["Competency To Training ID"]) : null,
        compId: competency?.competencyId || null,
        trainingName: row["Training Name"]?.trim() || "",
      };
    },
  },

  // =========================
  // 12. LEARNING AND DEVELOPMENT
  // =========================
  learningAndDevelopment: {
    requiredFields: [
      "Employee Name",
      "Competency Name",
      "Tranining Name",
      "Start Date",
      "End Date",
      "Expected Outcome Level",
    ],
    initTrackers: () => ({ ids: new Set() }),

    getColumns: (contextData) => [
      { field: "ID", headerName: "ID", width: 70, editable: true, type: "number" },

      {
        field: "Employee Name",
        headerName: "Employee Name",
        width: 180,
        editable: true,
        renderEditCell: (p) =>
          RenderDropdown(
            p,
            contextData.employees?.map((e) => e.name),
            {
              onChange: (newValue) => {
                p.api.setEditCellValue({
                  id: p.id,
                  field: "Employee Name",
                  value: newValue,
                });

                //RESET DEPENDENT FIELDS
                p.api.setEditCellValue({ id: p.id, field: "Competency Name", value: "" });
                p.api.setEditCellValue({ id: p.id, field: "Tranining Name", value: "" });
                p.api.setEditCellValue({ id: p.id, field: "Expected Outcome Level", value: "" });
                p.api.setEditCellValue({ id: p.id, field: "Achieved Level", value: "" });
              },
            }
          ),
      },

      // 2. Competency Name (RESTRICTED BY EMPLOYEE'S JOB)
      {
        field: "Competency Name",
        headerName: "Competency",
        width: 180,
        editable: true,
        renderEditCell: (p) => {
          const selectedEmp = contextData.employees?.find(
            (e) =>
              (e.name ?? "").trim().toLowerCase() ===
              (p.row["Employee Name"] ?? "").trim().toLowerCase()
          );

          if (!selectedEmp) return RenderDropdown(p, ["<-- Select Employee First"]);

          // Filter by Job Requirements
          const jobCompLevelIds = contextData.competenciesToJob
            ?.filter((cj) => cj.jobId === selectedEmp.jobId)
            .map((cj) => cj.compLevelId);

          const jobCompetencyIds = [
            ...new Set(
              jobCompLevelIds
                ?.map((clId) => {
                  const level = contextData.compLevels?.find((lv) => lv.competencyLevelId === clId);
                  return level ? level.competencyId : null;
                })
                .filter(Boolean)
            ),
          ];

          const filteredComps = contextData.competencies
            ?.filter((c) => jobCompetencyIds.includes(c.competencyId))
            .map((c) => c.name);

          if (!filteredComps || filteredComps.length === 0) {
            return RenderDropdown(p, ["No competencies found for that job"]);
          }

          // return RenderDropdown(p, filteredComps || []);

          return RenderDropdown(p, filteredComps || [], {
            onChange: (newValue) => {
              // 🔥 RESET dependent fields when competency changes
              p.api.setEditCellValue({ id: p.id, field: "Tranining Name", value: "" });
              p.api.setEditCellValue({ id: p.id, field: "Expected Outcome Level", value: "" });
              p.api.setEditCellValue({ id: p.id, field: "Achieved Level", value: "" });
            },
          });
        },
      },
      // 3. Training Name (RESTRICTED BY CURRENT COMPETENCY)
      {
        field: "Tranining Name",
        headerName: "Training Name",
        width: 200,
        editable: true,
        renderEditCell: (p) => {
          const selectedComp = contextData.competencies?.find(
            (c) =>
              (c.name ?? "").trim().toLowerCase() ===
              (p.row["Competency Name"] ?? "").trim().toLowerCase()
          );

          if (!selectedComp) return RenderDropdown(p, ["<-- Select Competency First"]);

          let filteredTrainings =
            contextData.compToTraining
              ?.filter((t) => t.compId === selectedComp.competencyId)
              .map((t) => t.trainingName) || [];

          if (filteredTrainings.length === 0) filteredTrainings = [" No Trainings Found"];

          return RenderDropdown(p, filteredTrainings || []);
        },
      },

      { field: "Start Date", headerName: "Start Date", width: 120, editable: true },
      { field: "End Date", headerName: "End Date", width: 120, editable: true },

      // 4. Expected Outcome Level (RESTRICTED BY CURRENT COMPETENCY)
      {
        field: "Expected Outcome Level",
        headerName: "Expected Level",
        width: 160,
        editable: true,
        renderEditCell: (p) => {
          const selectedComp = contextData.competencies?.find(
            (c) =>
              (c.name ?? "").trim().toLowerCase() ===
              (p.row["Competency Name"] ?? "").trim().toLowerCase()
          );

          let filteredLevels =
            contextData.compLevels
              ?.filter((l) => l.competencyId === selectedComp?.competencyId)
              .map((l) => l.levelName) || [];

          if (filteredLevels.length === 0) filteredLevels = ["No Levels Found"];

          return RenderDropdown(p, filteredLevels || []);
        },
      },

      // 5. Achieved Level (RESTRICTED BY CURRENT COMPETENCY)
      {
        field: "Achieved Level",
        headerName: "Achieved Level",
        width: 160,
        editable: true,
        renderEditCell: (p) => {
          if (!p.row.ID) {
            // If ID is null, do not display dropdown
            return <span style={{ color: "#999" }}>N/A</span>;
          }
          const selectedComp = contextData.competencies?.find(
            (c) =>
              (c.name ?? "").trim().toLowerCase() ===
              (p.row["Competency Name"] ?? "").trim().toLowerCase()
          );
          let filteredLevels =
            contextData.compLevels
              ?.filter((l) => l.competencyId === selectedComp?.competencyId)
              .map((l) => l.levelName) || [];

          if (filteredLevels.length === 0) filteredLevels = ["No Levels Found"];

          return RenderDropdown(p, filteredLevels || []);
        },
      },
    ],

    validateRow: (row, context, trackers) => {
      const errors = [];
      const {
        models: existingLD,
        compToTraining,
        employees,
        compLevels,
        competencies,
        competenciesToJob,
      } = context;

      const id = row["ID"] ? Number(row["ID"]) : null;
      const trainingName = row["Tranining Name"]?.trim().toLowerCase();
      const employeeName = row["Employee Name"]?.trim().toLowerCase();
      const competencyName = row["Competency Name"]?.trim().toLowerCase();
      const expectedLevelName = row["Expected Outcome Level"]?.trim().toLowerCase();
      const achievedLevelName = row["Achieved Level"]?.trim().toLowerCase();

      // 1. Validate ID
      if (id !== null) {
        if (trackers.ids.has(id)) errors.push("Duplicate ID in file");
        else trackers.ids.add(id);

        if (!existingLD.some((item) => item.id === id)) {
          errors.push("Record ID does not exist");
        }
      }

      // 2. Employee Lookup
      const employee = employees.find((e) => (e.name ?? "").trim().toLowerCase() === employeeName);
      if (!employeeName) {
        errors.push("Employee Name is required");
      } else if (!employee) {
        errors.push(`Employee '${employeeName}' not found`);
      }

      // 3. Competency Lookup & Job Requirement Validation (Your 3-step logic)
      const competency = competencies.find(
        (c) => (c.name ?? "").trim().toLowerCase() === competencyName
      );
      if (!competencyName) {
        errors.push("Competency Name is required");
      } else if (!competency) {
        errors.push(`Competency '${competencyName}' not found`);
      } else if (employee) {
        // --- START OF YOUR LOGIC ---
        // 1. Find all compLevelIds for this employee's job
        const jobCompLevelIds = competenciesToJob
          .filter((cj) => cj.jobId === employee.jobId)
          .map((cj) => cj.compLevelId);

        // 2. Resolve those levels to Competency IDs
        const jobCompetencyIds = jobCompLevelIds
          .map((clId) => {
            const level = compLevels.find((lv) => lv.competencyLevelId === clId);
            return level ? level.competencyId : null;
          })
          .filter(Boolean);

        // 3. Verify the selected competency is in that list
        if (!jobCompetencyIds.includes(competency.competencyId)) {
          errors.push(
            `Competency '${competencyName}' is not assigned to the job of '${employeeName}'`
          );
        }
        // --- END OF YOUR LOGIC ---
      }

      const training = compToTraining.find(
        (t) =>
          (t.trainingName ?? "").trim().toLowerCase() === trainingName &&
          t.compId === competency?.competencyId
      );
      if (!trainingName) {
        errors.push("Training Name is required");
      } else if (!training) {
        errors.push(`Training '${trainingName}' not found`);
      } else if (competency && training.compId !== competency.competencyId) {
        errors.push(`Training '${trainingName}' does not belong to Competency '${competencyName}'`);
      }

      const expectedLvl = compLevels.find(
        (l) =>
          (l.levelName ?? "").trim().toLowerCase() === expectedLevelName &&
          l.competencyId === competency?.competencyId
      );

      if (!expectedLevelName) {
        errors.push("Expected Outcome Level is required");
      } else if (!expectedLvl) {
        errors.push(
          `Level '${expectedLevelName}' is not defined for Competency '${competencyName}'`
        );
      }

      // 6. Achieved Level Validation (Optional, but must belong to selected Competency)
      if (achievedLevelName) {
        const achievedLvl = compLevels.find(
          (l) =>
            (l.levelName ?? "").trim().toLowerCase() === achievedLevelName &&
            l.competencyId === competency?.competencyId
        );

        if (!achievedLvl) {
          errors.push(
            `Achieved Level '${achievedLevelName}' is not defined for Competency '${competencyName}'`
          );
        }
      }

      const dateRegex = /^(0?[1-9]|[12][0-9]|3[01])[\/\-](0?[1-9]|1[0-2])[\/\-]\d{4}$/;

      if (row["Start Date"] && !dateRegex.test(row["Start Date"])) {
        errors.push("Start Date must be dd/MM/yyyy");
      }

      if (row["End Date"] && !dateRegex.test(row["End Date"])) {
        errors.push("End Date must be dd/MM/yyyy");
      }

      return errors;
    },

    mapToDTO: (row, context) => {
      // Sanitizing keys for case-insensitive lookup
      const searchTraining = String(row["Tranining Name"] || "")
        .trim()
        .toLowerCase();
      const searchEmployee = String(row["Employee Name"] || "")
        .trim()
        .toLowerCase();

      const searchCompetency = String(row["Competency Name"] || "")
        .trim()
        .toLowerCase();
      const searchExpected = String(row["Expected Outcome Level"] || "")
        .trim()
        .toLowerCase();
      const searchAchieved = String(row["Achieved Level"] || "")
        .trim()
        .toLowerCase();

      const employee = context.employees?.find(
        (e) => (e.name ?? "").trim().toLowerCase() === searchEmployee
      );

      const competency = context.competencies?.find(
        (c) => (c.name ?? "").trim().toLowerCase() === searchCompetency
      );

      const training = context.compToTraining?.find(
        (t) =>
          (t.trainingName ?? "").trim().toLowerCase() === searchTraining &&
          t.compId === competency?.competencyId
      );

      const expected = context.compLevels?.find(
        (l) =>
          (l.levelName ?? "").trim().toLowerCase() === searchExpected &&
          l.competencyId === competency?.competencyId
      );

      // Find Achieved Level for the competency
      const achieved = context.compLevels?.find(
        (l) =>
          (l.levelName ?? "").trim().toLowerCase() === searchAchieved &&
          l.competencyId === competency?.competencyId
      );

      return {
        id: row["ID"] ? Number(row["ID"]) : null,
        trainingId: training?.id || null, // Resolves to training primary key
        // employeeId: employee?.id || null, // Resolves to employee primary key
        employeeUuid: employee?.id || null,
        startDate: parseDate(row["Start Date"]), // dd/MM/yyyy -> YYYY-MM-DD
        endDate: parseDate(row["End Date"]), // dd/MM/yyyy -> YYYY-MM-DD
        expectedOutcomeLevel: expected?.competencyLevelId || null, // Resolves to level primary key
        // achievedLevel: achieved?.competencyLevelId || null, // Resolves to level primary key
        achievedLevel: row["ID"] && achieved?.competencyLevelId ? achieved.competencyLevelId : null,
      };
    },
  },
};
